import { MESSAGE_STATUSES } from '../../models/messaging-message';
import { concat, fn } from '@ember/helper';
import { eq } from 'ember-truth-helpers';
import { formatReadAtDate } from '../../utils/messaging/format-date';
import { noop } from 'ember-composable-helpers/helpers/noop';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import AttachTooltip from 'ember-attacher/components/attach-tooltip';
import Component from '@glimmer/component';
import styles from './message-subtext.module.scss';

export default class ConversationMessageMessageSubtext extends Component {
  @service intl;

  get shouldShowReadIndicator() {
    return (
      Object.keys(this.args.conversation.counterpartReadAt).length > 0 &&
      this.args.message.id === this.args.conversation.lastReadOwnMessage?.id
    );
  }

  get validCounterpartReadAt() {
    // Filters out readAts from older messages.
    return Object.entries(this.args.conversation.counterpartReadAt).filter(([_, value]) => {
      return new Date(value) > new Date(this.args.message.createdAt);
    });
  }

  get shouldShowNamesInReadAts() {
    return this.args.conversation.isGroup || this.args.conversation.isCouple;
  }

  get readReceiptEntries() {
    if (this.args.message.deletedAt && Array.isArray(this.args.message.readMemberships)) {
      return this.args.message.readMemberships.map(m => ({
        userId: m.user_id,
        readAt: m.read_at,
      }));
    }
    return this.validCounterpartReadAt.map(([userId, readAt]) => ({ userId, readAt }));
  }

  getUserName(userId) {
    const user = this.args.conversation.users?.find(u => String(u.id) === String(userId));
    return user?.name ?? null;
  }

  get readAts() {
    return this.readReceiptEntries.map(({ userId, readAt }) => {
      const name = this.getUserName(userId);
      return formatReadAtDate(this.intl, readAt, this.shouldShowNamesInReadAts ? name : null);
    });
  }

  get hasMultipleReadReceipts() {
    return this.readAts.length > 1;
  }

  get singleReadAtLine() {
    return this.readAts[0] ?? null;
  }

  get showReadByPrefixForSingle() {
    if (this.readReceiptEntries.length !== 1) return false;
    const userId = this.readReceiptEntries[0].userId;
    return this.shouldShowNamesInReadAts && this.getUserName(userId) !== null;
  }

  get onResendHandler() {
    return this.args.onResend || noop;
  }

  get shouldShowReadReceipts() {
    return this.args.message.deletedAt || this.shouldShowReadIndicator;
  }

  <template>
    <div class="{{styles.component}} message-subtext" data-test-message-subtext>
      {{#if this.shouldShowReadReceipts}}
        {{#if this.hasMultipleReadReceipts}}
          <p class="read-at" data-test-read-at>
            {{t "emc.read_at.read_by"}}
            <span class="read-by-people-count" data-test-read-by-people-count>
              {{t "emc.read_at.people_count" count=this.readAts.length}}
              <AttachTooltip @placement="top" @arrow={{true}} @animation="fade" class="emc-tooltip">
                <div>
                  {{#each this.readAts as |line|}}
                    <div>{{line}}</div>
                  {{/each}}
                </div>
              </AttachTooltip>
            </span>
          </p>
        {{else if this.singleReadAtLine}}
          <p class="read-at" data-test-read-at>
            {{#if this.showReadByPrefixForSingle}}
              {{t "emc.read_at.read_by"}}
            {{/if}}
            {{this.singleReadAtLine}}
          </p>
        {{/if}}
      {{else if (eq @message.status MESSAGE_STATUSES.FAILED)}}
        <span
          class="failed"
          role="button"
          {{on "click" (fn this.onResendHandler @message)}}
          data-test-resend
        >
          {{t "emc.message_status.failed"}}
        </span>
      {{else}}
        {{t (concat "emc.message_status." @message.status)}}
      {{/if}}
    </div>
  </template>
}
